import { Menu } from 'antd';
import { type ItemType, type MenuItemType } from 'antd/es/menu/interface';
import React from 'react';

import Link from './link';

export interface SidebarMenuItem {
  key: string;
  to: string;
  label: string | React.ReactElement;
}

export interface SidebarMenuGroup {
  key: string;
  label: string;
  items: SidebarMenuItem[];
}

export type SidebarMenuNode = SidebarMenuItem | SidebarMenuGroup;

interface SidebarMenuProps {
  items: SidebarMenuNode[];
}

const isGroup = (node: SidebarMenuNode): node is SidebarMenuGroup =>
  Array.isArray((node as SidebarMenuGroup).items);

const toMenuItem = ({ key, to, label }: SidebarMenuItem): MenuItemType => ({
  key,
  label: <Link to={to} label={label} />,
});

function SidebarMenu({ items }: SidebarMenuProps): React.ReactElement {
  let hasRenderedGroup = false;

  const menuItems: ItemType[] = items.flatMap(
    (node: SidebarMenuNode): ItemType[] => {
      if (!isGroup(node)) {
        return [toMenuItem(node)];
      }

      const group: ItemType = {
        key: node.key,
        type: 'group',
        label: node.label,
        children: node.items.map(toMenuItem),
      };

      const result: ItemType[] = hasRenderedGroup
        ? [{ key: `${node.key}-divider`, type: 'divider' }, group]
        : [group];

      hasRenderedGroup = true;

      return result;
    },
  );

  return <Menu items={menuItems} />;
}

export default SidebarMenu;
